import { motion } from 'framer-motion';
import { Cookie, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/landing/Footer';

const lastUpdated = 'April 9, 2026';

const tableOfContents = [
  { id: 'what-are-cookies', title: '1. What Are Cookies' },
  { id: 'how-we-use', title: '2. How We Use Cookies' },
  { id: 'types-of-cookies', title: '3. Types of Cookies We Use' },
  { id: 'essential-cookies', title: '4. Essential Cookies' },
  { id: 'analytics-cookies', title: '5. Analytics' },
  { id: 'third-party-cookies', title: '6. Third-Party Cookies' },
  { id: 'managing-cookies', title: '7. Managing Cookies' },
  { id: 'changes', title: '8. Changes to This Policy' },
  { id: 'contact', title: '9. Contact' },
];

const cookieTypes = [
  {
    name: 'Essential Cookies',
    description: 'Required for SetOpenClaw to function. Cannot be disabled.',
    examples: ['Authentication session', 'CSRF protection', 'Cookie preference'],
    retention: 'Session to 1 year',
    canDisable: false,
  },
  {
    name: 'Functional Storage',
    description: 'Stores your preferences in browser localStorage for a smoother experience.',
    examples: ['Auth tokens', 'Language preference', 'Theme'],
    retention: 'Until you sign out or clear browser data',
    canDisable: false,
  },
  {
    name: 'Analytics Cookies',
    description: 'Help us understand aggregate, anonymous usage so we can improve the product.',
    examples: ['Page views', 'Navigation paths', 'Error tracking'],
    retention: 'Up to 2 years',
    canDisable: true,
  },
];

export function CookiesPage() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/60 to-teal-50/60" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="h-16 w-16 rounded-full gradient-primary flex items-center justify-center mx-auto mb-6">
                <Cookie className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Cookie Policy</h1>
              <p className="text-muted-foreground">Last updated: {lastUpdated}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16 flex-1">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-[280px_1fr] gap-8">
              <motion.aside
                className="lg:sticky lg:top-24 lg:self-start"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Card>
                  <CardContent className="p-4">
                    <h2 className="font-semibold mb-3 text-sm uppercase tracking-wider text-muted-foreground">
                      Table of Contents
                    </h2>
                    <nav className="space-y-1">
                      {tableOfContents.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => scrollToSection(item.id)}
                          className="flex items-center w-full text-left text-sm py-2 px-3 rounded-md hover:bg-muted transition-colors group"
                        >
                          <ChevronRight className="h-4 w-4 mr-2 text-muted-foreground group-hover:text-emerald-600" />
                          <span className="group-hover:text-emerald-600">{item.title}</span>
                        </button>
                      ))}
                    </nav>
                  </CardContent>
                </Card>
              </motion.aside>

              <motion.div
                className="prose prose-slate max-w-none"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Card>
                  <CardContent className="p-6 md:p-8 space-y-8">
                    <section id="what-are-cookies">
                      <h2 className="text-2xl font-bold mb-4">1. What Are Cookies</h2>
                      <p className="text-muted-foreground mb-4">
                        Cookies are small text files stored on your device when you visit a
                        website. SetOpenClaw also uses browser <strong>localStorage</strong> to keep
                        you signed in between visits. Throughout this policy, "cookies" refers to
                        both technologies.
                      </p>
                      <p className="text-muted-foreground">
                        Cookies can be "persistent" or "session" cookies. Persistent cookies remain
                        on your device until they expire or you delete them; session cookies are
                        cleared when you close your browser.
                      </p>
                    </section>

                    <section id="how-we-use">
                      <h2 className="text-2xl font-bold mb-4">2. How We Use Cookies</h2>
                      <p className="text-muted-foreground mb-4">SetOpenClaw uses cookies to:</p>
                      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                        <li>Keep you signed in between page loads and visits.</li>
                        <li>Remember your language and theme preferences.</li>
                        <li>Protect against CSRF and other security threats.</li>
                        <li>Understand aggregate usage so we can improve the product.</li>
                      </ul>
                      <p className="text-muted-foreground mt-4">
                        We do not use advertising cookies, retargeting pixels, or sell your
                        information to third-party advertisers.
                      </p>
                    </section>

                    <section id="types-of-cookies">
                      <h2 className="text-2xl font-bold mb-4">3. Types of Cookies We Use</h2>
                      <div className="space-y-4 mt-6 not-prose">
                        {cookieTypes.map((cookie) => (
                          <Card key={cookie.name} className="overflow-hidden">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between mb-2">
                                <h3 className="font-semibold">{cookie.name}</h3>
                                <span
                                  className={`text-xs px-2 py-1 rounded-full ${
                                    cookie.canDisable
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-amber-100 text-amber-700'
                                  }`}
                                >
                                  {cookie.canDisable ? 'Optional' : 'Required'}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground mb-3">
                                {cookie.description}
                              </p>
                              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="font-medium">Examples:</span>
                                  <ul className="list-disc pl-4 text-muted-foreground mt-1">
                                    {cookie.examples.map((example) => (
                                      <li key={example}>{example}</li>
                                    ))}
                                  </ul>
                                </div>
                                <div>
                                  <span className="font-medium">Retention:</span>
                                  <p className="text-muted-foreground mt-1">{cookie.retention}</p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </section>

                    <section id="essential-cookies">
                      <h2 className="text-2xl font-bold mb-4">4. Essential Cookies</h2>
                      <p className="text-muted-foreground mb-4">
                        Essential cookies are required for the basic operation of SetOpenClaw. They
                        enable:
                      </p>
                      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                        <li>
                          <strong>Authentication:</strong> keeping you signed in securely across
                          page loads.
                        </li>
                        <li>
                          <strong>Security:</strong> CSRF protection and session integrity.
                        </li>
                        <li>
                          <strong>Cookie preference:</strong> remembering whether you have
                          acknowledged this policy.
                        </li>
                      </ul>
                      <p className="text-muted-foreground mt-4">
                        These cannot be disabled because the platform cannot function without them.
                      </p>
                    </section>

                    <section id="analytics-cookies">
                      <h2 className="text-2xl font-bold mb-4">5. Analytics</h2>
                      <p className="text-muted-foreground">
                        We may use minimal, privacy-respecting analytics to understand aggregate
                        usage patterns — for example, which dashboard pages are visited most often.
                        These analytics do not track you across other websites and do not identify
                        you personally.
                      </p>
                    </section>

                    <section id="third-party-cookies">
                      <h2 className="text-2xl font-bold mb-4">6. Third-Party Cookies</h2>
                      <p className="text-muted-foreground mb-4">
                        Some cookies are placed by third-party services that SetOpenClaw uses to
                        operate the platform:
                      </p>
                      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                        <li>
                          <strong>Stripe:</strong> payment processing and fraud prevention.
                        </li>
                        <li>
                          <strong>Google OAuth:</strong> if you sign in with Google, Google may set
                          its own cookies subject to its privacy policy.
                        </li>
                      </ul>
                      <p className="text-muted-foreground mt-4">
                        These third parties operate under their own privacy and cookie policies. We
                        do not control how they collect or use the information.
                      </p>
                    </section>

                    <section id="managing-cookies">
                      <h2 className="text-2xl font-bold mb-4">7. Managing Cookies</h2>
                      <p className="text-muted-foreground mb-4">
                        Most web browsers let you control cookies through their settings:
                      </p>
                      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                        <li>
                          <strong>Chrome:</strong> Settings &gt; Privacy and security &gt; Cookies
                        </li>
                        <li>
                          <strong>Firefox:</strong> Options &gt; Privacy & Security &gt; Cookies
                        </li>
                        <li>
                          <strong>Safari:</strong> Preferences &gt; Privacy &gt; Cookies
                        </li>
                        <li>
                          <strong>Edge:</strong> Settings &gt; Privacy &gt; Cookies
                        </li>
                      </ul>
                      <p className="text-muted-foreground mt-4">
                        Note: disabling essential cookies will prevent you from signing in to
                        SetOpenClaw.
                      </p>
                    </section>

                    <section id="changes">
                      <h2 className="text-2xl font-bold mb-4">8. Changes to This Policy</h2>
                      <p className="text-muted-foreground">
                        We may update this Cookie Policy from time to time to reflect changes in
                        our practices or for other operational, legal, or regulatory reasons. We
                        will post any updates on this page with a new date.
                      </p>
                    </section>

                    <section id="contact">
                      <h2 className="text-2xl font-bold mb-4">9. Contact</h2>
                      <p className="text-muted-foreground">
                        Questions about our use of cookies? Email us at{' '}
                        <a
                          href="mailto:support@setopenclaw.com"
                          className="text-emerald-600 hover:underline"
                        >
                          support@setopenclaw.com
                        </a>
                        .
                      </p>
                    </section>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
